/* "Check your inbox" screen after signup / when sign-in needs a verified email, with a resend button (30 s cooldown). */
import "../../account/account.css";
import { useEffect, useState } from "react";
import { useResendVerification } from "../../../data/hooks";
import { ApiError } from "../../../data/provider";
import { useI18n } from "../../../i18n";
import { Alert, Button, Icon, errMsg, useToast } from "../../../ui";

export default function CheckInbox({ email, onBack, backLabel, title }: { email: string; onBack?: () => void; backLabel?: string; title?: string }) {
  const { L } = useI18n();
  const toast = useToast();
  const m = useResendVerification();
  const [wait, setWait] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);
  const resend = async () => {
    setErr(null);
    try {
      await m.mutateAsync(email);
      toast(L("A new link is on its way", "تم إرسال رابط جديد"));
      setWait(30);
    } catch (e) {
      if (e instanceof ApiError && e.status === 429) setErr(L("Too many requests — please wait a few minutes and try again.", "طلبات كثيرة — انتظر بضع دقائق ثم حاول مجددًا."));
      else setErr(errMsg(e));
    }
  };
  return (
    <div className="stack" data-testid="check-inbox">
      <div className="inbox-hero">
        <span className="ico"><Icon name="mail" /></span>
        <h1>{title ?? L("Check your inbox", "تحقق من بريدك")}</h1>
        <p className="muted" style={{ margin: 0 }}>
          {L("We sent a confirmation link to ", "أرسلنا رابط التأكيد إلى ")}<b dir="ltr" data-testid="check-inbox-email">{email}</b>.
          {L(" Open it to activate your account — the link is valid for 48 hours.", " افتحه لتفعيل حسابك — الرابط صالح لمدة 48 ساعة.")}
        </p>
      </div>
      <Alert tone="info" tint title={L("Didn't get it?", "لم يصلك؟")} desc={L("Check spam or promotions, and make sure the address is right. You can request a new link below.", "تحقق من مجلد الرسائل غير المرغوبة وتأكد من صحة العنوان، ويمكنك طلب رابط جديد أدناه.")} />
      {err && <Alert tone="bad" title={err} testId="resend-error" />}
      <div className="row" style={{ justifyContent: "center" }}>
        <Button icon="refresh" loading={m.isPending} disabled={wait > 0} onClick={resend} data-testid="btn-resend">
          {wait > 0 ? L(`Resend in ${wait}s`, `إعادة الإرسال خلال ${wait} ث`) : L("Resend the link", "إعادة إرسال الرابط")}
        </Button>
        {onBack && <Button variant="ghost" onClick={onBack} data-testid="btn-inbox-back">{backLabel ?? L("Use a different email", "استخدام بريد آخر")}</Button>}
      </div>
    </div>
  );
}
